import React from "react";
import { PageHeader } from "../components/layout/Shell";
import StatutoryShell from "../components/statutory/StatutoryShell";
import { StatutoryProvider } from "../components/statutory/StatutoryContext";
import { StatutoryBadge, StatutoryStatePanel } from "../components/statutory/StatutoryBadge";
import {
  jurisdictions, regimes, openQuestions, transitionGates,
} from "../mock/statutory/mockData";
import { AlertTriangle, TrendingUp, Shield, Clock, CheckCircle2, XCircle } from "lucide-react";

const KPI_CARDS = [
  { label: "Active Jurisdictions", value: "4", sub: "IN, AE (3 entities)", icon: TrendingUp, color: "text-blue-600" },
  { label: "Design-Ready Regimes", value: "4", sub: "India EPF/ESI + UAE Law 57", icon: CheckCircle2, color: "text-green-600" },
  { label: "Blocked Regimes", value: "7", sub: "Law 7/1999 + GCC + ADPF + Sharjah", icon: XCircle, color: "text-red-600" },
  { label: "Legal Basis Pending", value: "1", sub: "UAE Law 7/1999 — OQ-V4-5", icon: AlertTriangle, color: "text-amber-600" },
  { label: "Counsel Reviews", value: "5", sub: "SA, BH, KW, QA, OM — OQ-V4-6", icon: Shield, color: "text-orange-600" },
  { label: "Open ARB Gates", value: "4", sub: "TG-V4-1/2/5/6 awaiting decision", icon: Clock, color: "text-blue-600" },
];

const ALERTS = [
  { state: "COUNSEL_REVIEW_REQUIRED", title: "5 GCC nationals — counsel required", detail: "OQ-V4-6 unresolved", link: "/statutory/open-questions" },
  { state: "LEGAL_BASIS_PENDING", title: "UAE Law 7/1999 rule versions blocked", detail: "OQ-V4-5 — V7-01..V7-08 unverified", link: "/statutory/rule-versions" },
  { state: "CLASSIFICATION_AMBIGUOUS", title: "1 person in analyst review queue", detail: "Omar Al-Shamsi — conflicting Article 2 inputs", link: "/statutory/eligibility" },
  { state: "OPEN", title: "4 ARB gates awaiting decision", detail: "TG-V4-1, TG-V4-2, TG-V4-5, TG-V4-6 submitted", link: "/statutory/open-questions" },
];

function JurisdictionPanel({ jur }) {
  const jurisdictionRegimes = regimes.filter((r) => r.jurisdiction === jur.id);
  const statusColors = {
    DESIGN_READY: "bg-green-50 border-green-200",
    PARTIAL: "bg-amber-50 border-amber-200",
    BLOCKED: "bg-red-50 border-red-200",
  };

  return (
    <div className={`rounded-lg border p-4 ${statusColors[jur.overallStatus] || "bg-card border-border"}`} data-testid={`jurisdiction-panel-${jur.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">{jur.flag}</span>
          <div>
            <div className="font-semibold text-sm text-foreground">{jur.code}</div>
            <div className="text-xs text-muted-foreground">{jur.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {jur.openOQs > 0 && (
            <span className="text-xs bg-red-100 text-red-700 border border-red-200 rounded px-1.5 py-0.5">{jur.openOQs} open OQs</span>
          )}
          <StatutoryBadge state={jur.overallStatus} />
        </div>
      </div>
      <div className="space-y-1.5">
        {jurisdictionRegimes.slice(0, 5).map((regime) => (
          <div key={regime.code} className="flex items-center justify-between py-1 border-t border-border/40 first:border-0">
            <span className="text-xs text-foreground/80 font-mono">{regime.code}</span>
            <div className="flex items-center gap-1.5">
              <StatutoryBadge state={regime.legalSourceStatus} size="xs" showIcon={false} />
              <StatutoryBadge state={regime.activationStatus} />
              {regime.blockingOQ && (
                <span className="text-xs text-red-600 font-mono">→ {regime.blockingOQ}</span>
              )}
            </div>
          </div>
        ))}
        {jurisdictionRegimes.length > 5 && (
          <div className="text-xs text-muted-foreground pt-1">+{jurisdictionRegimes.length - 5} more regimes</div>
        )}
      </div>
    </div>
  );
}

function ARBGateSummary() {
  const openGates = transitionGates.filter((g) => g.gateStatus !== "PASS");
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">ARB Gate Summary</div>
      <div className="space-y-2">
        {openGates.map((gate) => (
          <div key={gate.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-border/50 last:border-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs font-mono font-bold text-foreground shrink-0">{gate.id}</span>
              <span className="text-xs text-muted-foreground truncate">{gate.title}</span>
            </div>
            <StatutoryBadge state={gate.gateStatus === "EXTERNALLY_BLOCKED" ? "EXTERNALLY_BLOCKED" : "OPEN"} />
          </div>
        ))}
      </div>
      <div className="mt-3 pt-2 border-t border-border">
        <p className="text-xs text-muted-foreground">Gates PASS = 0 / 6 — Implementation remains BLOCKED / NOT AUTHORIZED until ARB issues explicit decision.</p>
      </div>
    </div>
  );
}

export default function StatutoryOverview() {
  return (
    <>
      <PageHeader
        eyebrow="Statutory Configuration"
        title="Overview Dashboard"
        description="Jurisdiction and regime readiness — current canonical state."
      />
      <StatutoryShell>
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {KPI_CARDS.map((card) => (
            <div key={card.label} className="rounded-lg border border-border bg-card p-4" data-testid={`kpi-${card.label}`}>
              <card.icon className={`h-4 w-4 mb-2 ${card.color}`} />
              <div className="text-2xl font-bold text-foreground">{card.value}</div>
              <div className="text-xs font-medium text-foreground mt-0.5">{card.label}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{card.sub}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Jurisdiction Panels */}
          <div className="lg:col-span-2 space-y-4">
            <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Jurisdiction Readiness</div>
            {jurisdictions.map((jur) => (
              <JurisdictionPanel key={jur.id} jur={jur} />
            ))}
          </div>

          {/* Right column */}
          <div className="space-y-4">
            {/* Alert Feed */}
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Active Alerts</div>
              <div className="space-y-3">
                {ALERTS.map((alert, i) => (
                  <div key={i} className="flex items-start gap-2.5 pb-3 border-b border-border/50 last:border-0">
                    <StatutoryBadge state={alert.state} showIcon={true} />
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-foreground">{alert.title}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">{alert.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <ARBGateSummary />

            {/* Open OQ count */}
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="text-xs font-bold text-amber-800 uppercase tracking-wider mb-2">Open Questions</div>
              {openQuestions.map((oq) => (
                <div key={oq.id} className="flex items-center justify-between py-1 border-b border-amber-100 last:border-0">
                  <span className="text-xs font-mono text-amber-900">{oq.id}</span>
                  <StatutoryBadge state={oq.status === "OPEN" ? "OPEN" : oq.status} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </StatutoryShell>
    </>
  );
}
